import { useId, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { describeError } from '@/features/auth/api';
import { cn } from '@/lib/cn';
import {
  networksFor,
  usePayoutAddresses,
  useRemovePayoutAddress,
  useSavePayoutAddress,
  type PayoutAddress,
  type PayoutAsset,
  type PayoutNetwork,
} from '../payout-addresses-api';
import { ProfileCard } from './ProfileCard';

const assets: { value: PayoutAsset; name: string; hint: string }[] = [
  { value: 'USDT', name: 'USDT', hint: 'Tether. Ağı sen seçiyorsun.' },
  { value: 'TRX', name: 'TRX', hint: 'Tron. Yalnızca Tron ağında.' },
];

/**
 * Where a member's race prizes should be sent.
 *
 * Addresses, not a wallet: there is no balance here and nothing that sends. The
 * prizes are paid by hand, and this is the detail that was missing from that.
 *
 * The network is a choice the member makes explicitly, and the warning beside it
 * is not decoration — USDT on the wrong chain is gone, and no amount of support
 * afterwards brings it back.
 */
export function PayoutAddressesCard() {
  const addresses = usePayoutAddresses();
  const [editing, setEditing] = useState<PayoutAsset | null>(null);

  const byAsset = new Map(
    (addresses.data ?? []).map((entry) => [entry.asset as PayoutAsset, entry]),
  );

  return (
    <ProfileCard
      title="Ödeme adreslerim"
      description="Yarışma ödüllerini nereye göndereceğimizi buraya yaz. Ödemeler elle yapılır."
    >
      {addresses.isLoading ? (
        <p className="py-8 text-center text-sm text-surface-500" role="status">
          Yükleniyor…
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {assets.map((asset) => (
            <AssetRow
              key={asset.value}
              asset={asset}
              saved={byAsset.get(asset.value)}
              isEditing={editing === asset.value}
              onToggle={() => setEditing(editing === asset.value ? null : asset.value)}
              onSaved={() => setEditing(null)}
            />
          ))}

          <p className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5 text-xs leading-relaxed text-amber-200/90">
            <svg
              aria-hidden
              viewBox="0 0 24 24"
              className="mt-0.5 size-4 shrink-0 fill-none stroke-current"
              strokeWidth={1.8}
            >
              <path d="M12 9v5M12 17.5v.01" strokeLinecap="round" />
              <path d="M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0z" />
            </svg>
            <span>
              Ağı doğru seçtiğinden emin ol. Yanlış ağa gönderilen kripto{' '}
              <strong>geri alınamaz</strong> — adresin formatını kontrol ediyoruz ama ağını senin
              yerine tahmin edemeyiz.
            </span>
          </p>
        </div>
      )}
    </ProfileCard>
  );
}

interface AssetRowProps {
  asset: (typeof assets)[number];
  saved: PayoutAddress | undefined;
  isEditing: boolean;
  onToggle: () => void;
  onSaved: () => void;
}

function AssetRow({ asset, saved, isEditing, onToggle, onSaved }: AssetRowProps) {
  return (
    <div
      className={cn(
        'rounded-xl border transition',
        saved ? 'border-surface-800 bg-surface-950/50' : 'border-surface-800 bg-surface-950/20',
      )}
    >
      <div className="flex flex-wrap items-center gap-3 p-3 sm:gap-4 sm:p-4">
        <span
          aria-hidden
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-full text-xs font-bold',
            asset.value === 'USDT'
              ? 'bg-emerald-500/15 text-emerald-300'
              : 'bg-red-500/15 text-red-300',
          )}
        >
          {asset.value}
        </span>

        <div className="min-w-0 flex-1">
          {saved ? (
            <>
              <p className="text-xs font-medium uppercase tracking-widest text-surface-500">
                {saved.networkLabel}
              </p>
              {/* break-all, not truncate: an address is checked character by
                  character, and a member cannot verify one that ends in "…". */}
              <p className="break-all font-mono text-xs text-surface-100">{saved.address}</p>
            </>
          ) : (
            <>
              <p className="font-medium text-surface-100">{asset.name}</p>
              <p className="text-xs text-surface-500">{asset.hint}</p>
            </>
          )}
        </div>

        <Button
          type="button"
          variant="ghost"
          className="border border-surface-700 px-4 py-2 text-sm"
          aria-expanded={isEditing}
          onClick={onToggle}
        >
          {isEditing ? 'Kapat' : saved ? 'Değiştir' : 'Ekle'}
        </Button>
      </div>

      {isEditing ? (
        <div className="border-t border-surface-800 p-3 sm:p-4">
          <AddressForm asset={asset.value} saved={saved} onSaved={onSaved} />
        </div>
      ) : null}
    </div>
  );
}

function AddressForm({
  asset,
  saved,
  onSaved,
}: {
  asset: PayoutAsset;
  saved: PayoutAddress | undefined;
  onSaved: () => void;
}) {
  const networks = networksFor[asset];
  const networkId = useId();
  const addressId = useId();

  const [network, setNetwork] = useState<PayoutNetwork>(
    (saved?.network as PayoutNetwork | undefined) ?? networks[0]!.value,
  );
  const [address, setAddress] = useState(saved?.address ?? '');

  const save = useSavePayoutAddress();
  const remove = useRemovePayoutAddress();

  const trimmed = address.trim();
  const pending = save.isPending || remove.isPending;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();

        if (trimmed === '') {
          return;
        }

        save.mutate({ asset, network, address: trimmed }, { onSuccess: onSaved });
      }}
    >
      {save.isError ? <Alert tone="error">{describeError(save.error)}</Alert> : null}

      {networks.length > 1 ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={networkId} className="text-xs font-medium text-surface-400">
            Ağ
          </label>
          <select
            id={networkId}
            value={network}
            onChange={(event) => setNetwork(event.target.value as PayoutNetwork)}
            className="min-h-11 rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 outline-none transition focus:border-brand-500"
          >
            {networks.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p className="text-xs text-surface-500">
          Ağ: <span className="text-surface-300">{networks[0]!.label}</span> — {asset} yalnızca bu
          ağda var.
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor={addressId} className="text-xs font-medium text-surface-400">
          Cüzdan adresi
        </label>
        <input
          id={addressId}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          // Autocorrect and capitalisation are OFF, and that is load-bearing on a
          // phone: Base58 is case-sensitive and an EVM address carries its
          // checksum IN the capitalisation, so a helpful keyboard would corrupt
          // the address before it was ever submitted.
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          maxLength={64}
          placeholder={network === 'Tron' ? 'T…' : '0x…'}
          className="min-h-11 rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 font-mono text-sm text-surface-100 outline-none transition placeholder:text-surface-600 focus:border-brand-500"
        />
        <p className="text-xs text-surface-500">
          Cüzdanından kopyala-yapıştır yap. Adresin sağlama toplamını doğruluyoruz — bir harfi bile
          yanlışsa kaydetmeyiz.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" loading={save.isPending} disabled={pending || trimmed === ''}>
          Kaydet
        </Button>

        {saved ? (
          <Button
            type="button"
            variant="ghost"
            loading={remove.isPending}
            disabled={pending}
            onClick={() => remove.mutate(asset, { onSuccess: onSaved })}
          >
            Adresi kaldır
          </Button>
        ) : null}
      </div>

      {remove.isError ? <Alert tone="error">{describeError(remove.error)}</Alert> : null}
    </form>
  );
}
